/**
 * Selector telemetry registry (Rally US2040385).
 *
 * Consumers (Deal Summary sections, other UI regions) call
 * {@link registerSelectorTelemetry} at bootstrap to declare which selectors
 * the {@link ../middleware/selectorDeltaTelemetryMiddleware selector-delta
 * telemetry middleware} should evaluate on every dispatch. Registration is
 * decentralized: each section owns its subset, keyed by a stable dot-path
 * identifier used verbatim on the wire.
 *
 * DESIGN CONSTRAINTS:
 *   - **Idempotent by key**: re-registering the same `key` overwrites the
 *     previous entry rather than duplicating it. This lets hot-reload and
 *     test isolation work without accumulating stale entries.
 *   - **Stable order**: {@link getRegisteredSelectors} returns entries in
 *     registration order so downstream serialization (delta rows, snapshot
 *     rows) is deterministic across sessions with identical registration
 *     graphs. Deterministic key order matters for replay diffing.
 *   - **Type erasure at the boundary**: registrations arrive typed by their
 *     local `S` (state slice), but the registry stores them under a common
 *     `unknown`-state signature so the middleware can iterate uniformly.
 *     The middleware never inspects the state shape — it just calls the
 *     stored selector with the current store state.
 *   - **`resetSelectorRegistry` is TEST-ONLY**: production code must not
 *     call it. Marked accordingly.
 */

/**
 * Categorization of a selector's output for on-wire encoding.
 *
 *   - `scalar`     — primitive (`string | number | boolean | null | undefined`)
 *                    or shallow object with only primitive values. Emitted
 *                    with full `{ b, a }` before/after values.
 *   - `collection` — arrays, deep objects, or anything expensive to serialize.
 *                    Emitted with `{ h }` (8-char FNV hash) only; consumers
 *                    can detect that the collection changed but must fetch
 *                    the value from an anchor (or a separate snapshot) if
 *                    they need the payload.
 *
 * Choosing `collection` for a selector that is actually scalar is safe (just
 * wastes a bit of hash CPU); choosing `scalar` for a large object risks
 * blowing the per-event byte budget and will trigger chunking in the
 * middleware.
 */
export type SelectorShape = 'scalar' | 'collection';

/**
 * A single selector registration.
 *
 * @template S - The Redux state tree type the selector consumes. Kept
 *               generic on the boundary so consumers don't have to type
 *               through `unknown`; the registry itself stores the
 *               `unknown`-typed variant.
 */
export type SelectorRegistration<S = unknown> = {
    /**
     * Stable dot-path identifier for this selector on the wire (e.g.
     * `'dealSummary.fees.totalAmount'`). Consumers should pick a name that
     * unambiguously identifies the UI-visible value; NRQL queries and the
     * Python replay CLI use this exact string.
     *
     * Must be non-empty. Re-registering the same key overwrites the
     * previous entry.
     */
    key: string;

    /**
     * Selector function. Called with the entire Redux state and expected
     * to return the value the UI would render. Errors thrown by the
     * selector are swallowed by the middleware (never break the app);
     * a selector that throws is treated as `undefined` for delta purposes.
     */
    selector: (state: S) => unknown;

    /**
     * Payload shape for the wire encoding. See {@link SelectorShape}.
     *
     * Optional — defaults to `'scalar'` when omitted, since the overwhelming
     * majority of registered selectors return primitives. Only declare a shape
     * when the selector returns a collection (array / deep object), which makes
     * the exceptions stand out in a registration table.
     */
    shape?: SelectorShape;
};

/**
 * Registration entry as stored in the registry. Same shape as
 * {@link SelectorRegistration} but state-typed as `unknown` so the middleware
 * can iterate without knowing the consumer's state shape. `shape` is required
 * here because the registry fills the `'scalar'` default at store time.
 */
export type RegisteredSelector = SelectorRegistration<unknown> & { shape: SelectorShape };

// Registration order is preserved for deterministic output. Using an Array
// alongside a Map so we can maintain insertion order while still supporting
// O(1) idempotent overwrite by key.
const registeredKeys: string[] = [];
const registeredByKey: Map<string, RegisteredSelector> = new Map();

/**
 * Register one or more selectors for telemetry emission.
 *
 * Called once per consuming module at bootstrap. Safe to call repeatedly
 * with the same keys (later calls overwrite prior entries under the same
 * key, preserving original insertion position).
 *
 * @param entries - Selectors to register. Empty array is a no-op.
 */
export const registerSelectorTelemetry = <S = unknown>(entries: SelectorRegistration<S>[]): void => {
    for (const entry of entries) {
        if (!entry || typeof entry.key !== 'string' || entry.key.length === 0) continue;
        if (typeof entry.selector !== 'function') continue;
        // `shape` is optional and defaults to 'scalar'. Reject only an
        // explicitly-provided invalid shape.
        if (entry.shape !== undefined && entry.shape !== 'scalar' && entry.shape !== 'collection') continue;

        const stored: RegisteredSelector = {
            key: entry.key,
            selector: entry.selector as (state: unknown) => unknown,
            shape: entry.shape ?? 'scalar'
        };

        if (!registeredByKey.has(entry.key)) {
            registeredKeys.push(entry.key);
        }
        registeredByKey.set(entry.key, stored);
    }
};

/**
 * Get all registered selectors, in registration order.
 *
 * Returned array is a fresh copy — callers may iterate freely without
 * affecting the registry state. Middleware calls this once per action.
 */
export const getRegisteredSelectors = (): RegisteredSelector[] => {
    const out: RegisteredSelector[] = [];
    for (const key of registeredKeys) {
        const entry = registeredByKey.get(key);
        if (entry) out.push(entry);
    }
    return out;
};

/**
 * TEST-ONLY: clear the registry. Do not call from production code.
 *
 * Provided so unit tests that exercise registration can reset state between
 * cases without cross-test pollution.
 */
export const resetSelectorRegistry = (): void => {
    registeredKeys.length = 0;
    registeredByKey.clear();
};
